import { GuestSessionService } from "@/client/services/guest-session";

const LOCAL_STORAGE_KEY = "guest-resume";

/**
 * Utility functions for syncing guest resume data between localStorage and GuestSession
 */
export class GuestResumeSync {
  private static savePromise: Promise<boolean> | null = null; // Prevent concurrent saves
  /**
   * Save resume data to both GuestSession and localStorage for redundancy (with race condition protection)
   */
  static async saveResumeData(resumeData: any): Promise<boolean> {
    // If already saving, wait for that to complete instead of creating duplicate saves
    if (this.savePromise) {
      return this.savePromise;
    }

    // Create new save promise
    this.savePromise = (async (): Promise<boolean> => {
      try {
        // Ensure guest session exists before saving
        const hasSession = await GuestSessionService.hasGuestSession();
        if (!hasSession) {
          try {
            await GuestSessionService.initializeSession();
          } catch (initError) {
            console.error("❌ Failed to initialize guest session:", initError);
            // Continue with localStorage-only save
          }
        }

        try {
          // Save to GuestSession first (primary storage)
          await GuestSessionService.saveResumeData(resumeData);
        } catch (sessionError) {
          console.error("❌ Failed to save to guest session:", sessionError);
          // Continue to localStorage fallback
        }

        // Also save to localStorage as fallback
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resumeData));
        return true;
      } catch (error) {
        console.error("Failed to save to guest session, using localStorage only:", error);

        // Fallback to localStorage only
        try {
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resumeData));
          return true;
        } catch (localError) {
          console.error("Failed to save to localStorage:", localError);
          return false;
        }
      } finally {
        // Clear the promise after completion
        this.savePromise = null;
      }
    })();

    return this.savePromise;
  }

  /**
   * Load resume data with priority: GuestSession > localStorage
   */
  static async loadResumeData(): Promise<any | null> {
    try {
      // Check guest session exists before loading
      const hasSession = await GuestSessionService.hasGuestSession();
      if (hasSession) {
        // 1. Try GuestSession first (most reliable)
        const sessionResponse = await GuestSessionService.getResumeData();
        if (sessionResponse.success && sessionResponse.resumeData) {
          return sessionResponse.resumeData;
        }
      }
    } catch (sessionError) {
      console.warn("Failed to load from guest session:", sessionError);
    }

    // 2. Fallback to localStorage
    try {
      const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localData) {
        const parsedData = JSON.parse(localData);

        // Try to sync to session for future use
        try {
          await GuestSessionService.saveResumeData(parsedData);
        } catch {
          // Silent fail - localStorage data is still usable
        }

        return parsedData;
      }
    } catch (localError) {
      console.error("Failed to load from localStorage:", localError);
    }

    return null;
  }

  /**
   * Clear resume data from both storage locations
   */
  static async clearResumeData(): Promise<void> {
    try {
      await GuestSessionService.clearResumeData();
    } catch (error) {
      console.warn("Failed to clear guest session data:", error);
    }

    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear localStorage data:", error);
    }

    console.log("🗑️ Guest resume data cleared");
  }

  /**
   * Check if guest has any resume data
   */
  static async hasResumeData(): Promise<boolean> {
    try {
      const sessionResponse = await GuestSessionService.getResumeData();
      if (sessionResponse.success && sessionResponse.resumeData) {
        return true;
      }
    } catch {
      // Continue to localStorage check
    }

    try {
      const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
      return !!localData;
    } catch {
      return false;
    }
  }
}
